/* ============================================================
   ZNAKI · audio/voice.js
   Pojedynczy grający dźwięk: własna głośność, panorama i filtr
   (dziedziczone z AudioChain), wpięty w magistralę grupy.

   Trzy tryby odtwarzania:
     loop: false          — jednorazowo
     loop: true           — pętla bez szwu (natywna, dla materiału zapętlonego)
     overlap: N (sekundy) — "przedłużanie": kolejne powtórzenie startuje
                            N sekund przed końcem poprzedniego i nakłada się
                            na nie. Tak przedłużamy akordy tła, które mają
                            wtopione łagodne wejście i wyjście.

   Odwrotność przedłużania to PRZYCIĘCIE: `duration` skraca dźwięk do
   zadanego czasu, a `release` wycisza go tuż przed cięciem, żeby nie
   został trzask. Tak gra glitch — próbki od autora są dłuższe niż samo
   rozdarcie obrazu, a mają się z nim kończyć równo. Razem z `offset`
   (losowe miejsce startu) z jednej próbki wychodzi wiele różnych zdarzeń.
   ============================================================ */

import AudioChain from './chain';

/* wyprzedzenie harmonogramu: kolejne powtórzenia planujemy z zapasem,
   żeby throttling timerów w karcie w tle nie zrobił dziury w dźwięku */
const LOOKAHEAD = 4.0;      /* [s] */
const SCHEDULE_TICK = 1000; /* [ms] */

class Voice extends AudioChain {

  /**
   * @param {AudioContext} ctx
   * @param {AudioBuffer} buffer
   * @param {object} opts — { volume, pan, cutoff, loop, overlap, rate, offset,
   *                        duration, release }
   */
  constructor(ctx, buffer, opts = {}) {
    super(ctx, opts);

    this.buffer  = buffer;
    this.loop    = !!opts.loop;
    this.overlap = Math.max(0, opts.overlap || 0);
    this.rate    = opts.rate || 1;
    this.offset  = opts.offset || 0;
    /* przycięcie: 0 = graj próbkę do końca */
    this.duration = Math.max(0, opts.duration || 0);
    this.release  = Math.max(0, opts.release || 0);

    this.isPlaying = false;
    this.onEnded = opts.onEnded || null;

    this._sources = [];
    this._nextAt = 0;
    this._timer = null;
    this._stopTimer = null;
  }

  /* --- odtwarzanie -------------------------------------------- */

  /**
   * @param {number} when   — czas AudioContext (0 = teraz)
   * @param {number} fadeIn — czas narastania [s] (0 = bez obwiedni)
   */
  start(when = 0, fadeIn = 0) {
    if (this.isPlaying) return this;
    const t0 = Math.max(when || 0, this.ctx.currentTime);

    /* Obwiednia wejścia i — przy przycięciu — wyjścia. Planujemy ją
       w jednym ciągu, bo fade() zaczyna od anulowania zdarzeń
       i skasowałby ustawione tu zero. */
    const cut = this.duration > 0 ? t0 + this.duration : 0;
    const rel = cut > 0 ? Math.min(this.release, this.duration * 0.5) : 0;
    if (fadeIn > 0 || rel > 0) {
      const p = this.gain.gain;
      const to = p.value;
      p.cancelScheduledValues(t0);
      p.setValueAtTime(fadeIn > 0 ? 0 : to, t0);
      if (fadeIn > 0) p.linearRampToValueAtTime(to, t0 + fadeIn);
      if (rel > 0) {
        /* przy bardzo krótkim dźwięku wejście może sięgać dalej niż
           początek wyciszenia — wtedy wyciszamy prosto z tego miejsca */
        const from = Math.max(cut - rel, t0 + fadeIn);
        if (from > t0) p.setValueAtTime(to, from);
        p.linearRampToValueAtTime(0, Math.max(cut, from + 0.005));
      }
      this._lastVolume = null;
    }

    this.isPlaying = true;

    if (this.overlap > 0) {
      this._nextAt = t0;
      this._schedule();
      this._timer = setInterval(() => this._schedule(), SCHEDULE_TICK);
    } else {
      this._spawn(t0, this.offset);
    }
    return this;
  }

  /**
   * Wyciszenie i zatrzymanie. Bez argumentu — natychmiast.
   * @param {number} fadeOut [s]
   */
  stop(fadeOut = 0) {
    if (!this.isPlaying) return this;
    this.isPlaying = false;

    /* najpierw zamykamy harmonogram — żadnych nowych powtórzeń */
    clearInterval(this._timer);
    this._timer = null;

    if (fadeOut > 0) {
      this.fade(0, fadeOut);
      this._stopTimer = setTimeout(() => this._teardown(), (fadeOut + 0.1) * 1000);
    } else {
      this._teardown();
    }
    return this;
  }

  /** czy głos wciąż zajmuje zasoby (gra lub się wycisza) */
  get isAlive() { return this.isPlaying || this._sources.length > 0; }

  destroy() {
    clearInterval(this._timer);
    clearTimeout(this._stopTimer);
    this._timer = this._stopTimer = null;
    this.isPlaying = false;
    this._teardown();
  }

  /* --- wewnętrzne --------------------------------------------- */

  /* zaplanuj powtórzenia mieszczące się w oknie wyprzedzenia */
  _schedule() {
    if (!this.isPlaying) return;
    const step = Math.max(0.1, this.buffer.duration / this.rate - this.overlap);
    const until = this.ctx.currentTime + LOOKAHEAD;
    let guard = 0;
    while (this._nextAt < until && guard++ < 32) {
      this._spawn(this._nextAt, 0);
      this._nextAt += step;
    }
  }

  /* utwórz i uruchom jedno źródło (AudioBufferSourceNode jest jednorazowy) */
  _spawn(at, offset) {
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.playbackRate.value = this.rate;
    src.loop = this.loop;
    src.connect(this.input);
    src.onended = () => {
      const i = this._sources.indexOf(src);
      if (i >= 0) this._sources.splice(i, 1);
      try { src.disconnect(); } catch (e) { /* nic */ }
      if (!this.loop && this.overlap === 0 && this.onEnded) this.onEnded(this);
    };
    src.start(at, offset || 0);
    /* przycięcie działa tylko na dźwiękach jednorazowych — pętla
       i przedłużanie mają swoje własne zasady długości */
    if (this.duration > 0 && !this.loop && this.overlap === 0) {
      src.stop(at + this.duration);
    }
    this._sources.push(src);
  }

  _teardown() {
    for (const src of this._sources.slice()) {
      try { src.onended = null; src.stop(); } catch (e) { /* już zatrzymany */ }
      try { src.disconnect(); } catch (e) { /* nic */ }
    }
    this._sources.length = 0;
    this.disconnect();
  }
}

export default Voice;
